import React, { useState, useMemo, useEffect, useCallback } from "react";
import ApiData from "../../../api/ApiData";
import User from "../../../api/User";
import ReportData from "../../../api/ReportData";
import { ListVersionReport } from "./ListVersionReport";
import { observer } from "mobx-react-lite";
import { runInAction } from "mobx";
import Loader from "../../Loader";
import { useNavigate } from "react-router-dom";
import { ExportToExcel } from "../../../Export/ExportXLSX";
import Memory from "../../../api/Memory";
import stateFilter from "../../../state/state.filter";
import { VisibleActiveSoft } from "../../company/VisibleActiveSoft";
import Media from "../../../api/Media";
import { ReportsManagerFilterBar } from "../ReportsManagerFilterBar";
import {
  filterRowsByInn,
  managerExcelCellsForInn,
  saveReportsShowMineOnly,
  useReportsShowMineOnly,
} from "../../../utils/reportManagerFilterUtils";
import { useApplyReportTemplate } from "../../../hooks/useApplyReportTemplate";
import { SaveReportTemplateButton } from "../SaveReportTemplateButton";
import ReportExportActionsColumn from "../ReportExportActionsColumn";
import { REPORT_TAB_VERSION } from "../../../utils/reportTemplatesStorage";
import {
  DIRECTIONS,
  resolveDirectionFromStorage,
} from "../../../utils/softVersionCompareUtils";
import {
  getSoftwareTypeFromSession,
  setSoftwareTypeInSession,
} from "../../../utils/softVersionClientTypeUtils";
import { ReportsVersionField } from "../../reports/ReportsVersionField";

export const VersionReport = observer(() => {
  const [valueVersion, setValueVersion] = useState("1.0.4.6");
  const [filterName, setFilterName] = useState(() => {
    const item = resolveDirectionFromStorage();
    Memory.setVersionReduction(item.memory);
    return item.key;
  });
  const [search, setSearch] = useState("");
  const [showMineOnly, setShowMineOnly] = useReportsShowMineOnly();
  const [softwareTypeMode, setSoftwareTypeMode] = useState(() =>
    getSoftwareTypeFromSession(),
  );

  const applyDirection = useCallback((label) => {
    const item = DIRECTIONS.find((d) => d.key === label) ?? DIRECTIONS[2];
    setFilterName(item.key);
    Memory.setVersionReduction(item.memory);
    sessionStorage.setItem("condition_version_client", item.key);
  }, []);

  const directionVersion = (e) => {
    try {
      const label = e.target?.dataset?.direction || e.target?.textContent;
      if (label === "<") applyDirection("Меньше");
      else if (label === "=") applyDirection("Равно");
      else if (label === ">") applyDirection("Больше");
      else if (DIRECTIONS.some((d) => d.key === label)) applyDirection(label);
    } catch (err) {
      console.error(err);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (ApiData.companys === null) {
      ApiData.getDataCompanys();
    }
  }, []);

  const refreshVersionReport = useCallback(() => {
    if (ReportData.versionReportSourcesReady) {
      return ReportData.computeSoftVersionReport(valueVersion);
    }
    return ReportData.reportSoftVersion(valueVersion);
  }, [valueVersion]);

  const applyTemplateFields = useCallback(
    async (fields) => {
      stateFilter.setParameter(
        "filter_active_client",
        Boolean(fields.filterActiveClient),
      );
      stateFilter.setLocalStorage(
        "filter_active_client",
        stateFilter.filter_active_client,
      );
      stateFilter.setParameter(
        "filter_active_server",
        Boolean(fields.filterActiveServer),
      );
      stateFilter.setLocalStorage(
        "filter_active_server",
        stateFilter.filter_active_server,
      );
      saveReportsShowMineOnly(Boolean(fields.showMineOnly));
      setShowMineOnly(Boolean(fields.showMineOnly));
      setSearch(fields.search ?? "");
      setValueVersion(fields.valueVersion ?? "1.0.4.6");
      applyDirection(fields.direction ?? "Больше");
      const nextType =
        fields.softwareTypeMode ?? getSoftwareTypeFromSession();
      setSoftwareTypeInSession(nextType);
      setSoftwareTypeMode(nextType);
    },
    [applyDirection, setShowMineOnly],
  );

  useApplyReportTemplate(REPORT_TAB_VERSION, applyTemplateFields);

  const getFieldsSnapshot = useCallback(
    () => ({
      valueVersion,
      direction: filterName,
      filterActiveClient: stateFilter.filter_active_client,
      filterActiveServer: stateFilter.filter_active_server,
      showMineOnly,
      search,
      softwareTypeMode,
    }),
    [filterName, search, showMineOnly, valueVersion, softwareTypeMode],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) {
        return;
      }
      await refreshVersionReport();
    })();
    return () => {
      cancelled = true;
    };
  }, [
    valueVersion,
    filterName,
    refreshVersionReport,
    stateFilter.filter_active_client,
    stateFilter.filter_active_server,
    softwareTypeMode,
  ]);

  const softVersionRows = ReportData.soft_version;

  const filteredItems = useMemo(() => {
    return filterRowsByInn(softVersionRows, {
      searchQuery: search,
      showMineOnly,
      currentUserId: User.user?.id,
      companys: ApiData.companys,
    });
  }, [softVersionRows, search, showMineOnly, User.user?.id, ApiData.companys]);

  const excel = useMemo(() => {
    return filteredItems.map((el) => ({
      Компания: el.name_company,
      ИНН: el.inn,
      ...managerExcelCellsForInn(el.inn, ApiData.companys),
      Количество: el.count,
      Условие: `${filterName} ${valueVersion}`,
    }));
  }, [filteredItems, filterName, valueVersion, ApiData.companys]);

  const hendlerValueVersion = (e) => {
    setValueVersion(e.target.value);
  };

  const onSoftwareTypeModeChange = useCallback(
    (mode) => {
      setSoftwareTypeInSession(mode);
      setSoftwareTypeMode(mode);
    },
    [],
  );

  const moveCompany = (e) => {
    runInAction(() => {
      ApiData.company.clients_soft = null;
      ApiData.company.clients_soft_data = null;
      ApiData.company.company = null;
    });
    sessionStorage.setItem("soft_version", valueVersion);
    sessionStorage.setItem("condition_version_client", filterName);
    navigate(`/company/${e.inn}`);
  };

  const setActiveClient = () => {
    stateFilter.setParameter(
      "filter_active_client",
      !stateFilter.filter_active_client,
    );
    stateFilter.setLocalStorage(
      "filter_active_client",
      stateFilter.filter_active_client,
    );
  };

  const setServerSoft = () => {
    stateFilter.setParameter(
      "filter_active_server",
      !stateFilter.filter_active_server,
    );
    stateFilter.setLocalStorage(
      "filter_active_server",
      stateFilter.filter_active_server,
    );
  };

  return (
    <section className="card reports-tab-panel-section" aria-label="Версия софта">
      <header className="reports-panel-head">
        <h2 className="dash-card-title">Сводный отчёт по версии софта</h2>
        <p className="reports-panel-desc">
          Компании с версией программного обеспечения относительно выбранного
          условия
        </p>
      </header>

      <div className="reports-toolbar reports-toolbar--version">
        <ReportsManagerFilterBar
          search={search}
          onSearchChange={(e) => setSearch(e.target.value)}
          showMineOnly={showMineOnly}
          onShowMineOnlyChange={setShowMineOnly}
        />
        <VisibleActiveSoft
          v4Layout
          onClickActive={setActiveClient}
          onClickServer={setServerSoft}
        />
        <div className="reports-version-controls">
          <div className="subscription-period-chips">
            {DIRECTIONS.map((d) => (
              <button
                key={d.key}
                type="button"
                data-direction={d.key}
                className={`subscription-period-chip${
                  filterName === d.key
                    ? " subscription-period-chip--active"
                    : ""
                }`}
                onClick={directionVersion}
              >
                {!Media.mobile ? d.key : d.short}
              </button>
            ))}
          </div>
          <ReportsVersionField
            value={valueVersion}
            onChange={hendlerValueVersion}
            softwareTypeMode={softwareTypeMode}
            onSoftwareTypeModeChange={onSoftwareTypeModeChange}
          />
          <ReportExportActionsColumn>
            <SaveReportTemplateButton
              tab={REPORT_TAB_VERSION}
              getFieldsSnapshot={getFieldsSnapshot}
            />
            <ExportToExcel
              fileName={`Отчёт по версии софта ${filterName} ${valueVersion}`}
              excel={excel}
            />
          </ReportExportActionsColumn>
        </div>
      </div>

      {!ReportData.flagVersion ? (
        <ListVersionReport
          moveCompany={moveCompany}
          valueVersion={valueVersion}
          searchQuery={search}
          items={filteredItems}
          companys={ApiData.companys}
        />
      ) : (
        <Loader />
      )}
    </section>
  );
});
